import React from 'react';

import { ActionIcon, createStyles, Select, Space, TextInput, Tooltip, useMantineTheme } from '@mantine/core';
import { DatePickerInput, DatesRangeValue } from '@mantine/dates';
import { IconFilterX, IconSearch, IconX } from '@tabler/icons-react';
import { MOVEMENT_TYPE_FILTER_OPTIONS } from 'types/enums';

import { WorkoutHistorySearchFilters } from '../types';

const today = new Date();

const useStyles = createStyles(theme => ({
  filters: {
    padding: theme.spacing.xl,
    paddingTop: theme.spacing.xs,
    paddingBottom: theme.spacing.xs,
    display: 'grid',
    gridTemplateColumns: 'auto auto auto min-content',
    gridTemplateRows: 'auto',
    gridTemplateAreas: `'search type dates clear'`,
    alignItems: 'center',
    gap: theme.spacing.sm,
    [theme.fn.smallerThan('lg')]: {
      gridTemplateColumns: 'auto auto min-content',
      gridTemplateRows: 'auto auto',
      gridTemplateAreas: `
        'search search clear'
        'type dates dates'
      `,
    },
    [theme.fn.smallerThan('sm')]: {
      gridTemplateColumns: 'auto min-content',
      gridTemplateAreas: `
        'search clear'
        'type type'
        'dates dates'
      `,
    },
  },
  search: { gridArea: 'search' },
  type: { gridArea: 'type' },
  dates: { gridArea: 'dates' },
  clear: { gridArea: 'clear' },
}));

const WorkoutHistoryFilters = ({
  searchFilters,
  initialFilterValues,
  setSearchFilters,
}: {
  searchFilters: WorkoutHistorySearchFilters;
  initialFilterValues: WorkoutHistorySearchFilters;
  setSearchFilters: React.Dispatch<React.SetStateAction<WorkoutHistorySearchFilters>>;
}): JSX.Element => {
  const theme = useMantineTheme();
  const { classes } = useStyles();

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchFilters(prev => ({ ...prev, search: e?.currentTarget?.value }));
  };
  const handleClearSearchFilter = (): void => {
    setSearchFilters(prev => ({ ...prev, search: '' }));
  };
  const handleTypeChange = (value: string | null): void => {
    setSearchFilters(prev => ({ ...prev, type: value ?? initialFilterValues.type }));
  };
  const handleDateRangeChange = (value: DatesRangeValue): void => {
    setSearchFilters(prev => ({ ...prev, dateRange: value }));
  };
  const handleClearFilters = (): void => {
    setSearchFilters(initialFilterValues);
  };

  return (
    <div className={classes.filters}>
      <TextInput
        className={classes.search}
        placeholder='Search by name'
        icon={<IconSearch size={theme.fontSizes.md} />}
        onChange={handleSearchChange}
        value={searchFilters.search}
        aria-label='Search workout history by name'
        rightSection={
          searchFilters.search ? (
            <ActionIcon onClick={handleClearSearchFilter} size='md' aria-label='Clear search'>
              <IconX size={theme.fontSizes.md} />
            </ActionIcon>
          ) : (
            <Space />
          )
        }
      />
      <Select
        className={classes.type}
        aria-label='Filter by type'
        data={MOVEMENT_TYPE_FILTER_OPTIONS}
        value={searchFilters.type}
        onChange={handleTypeChange}
      />
      <DatePickerInput
        className={classes.dates}
        value={searchFilters.dateRange}
        onChange={handleDateRangeChange}
        valueFormat='YYYY MMM DD'
        type='range'
        clearable
        {...{ placeholder: 'Select date range' }}
        maxDate={today}
        allowSingleDateInRange
      />
      <Tooltip label='Clear filters' className={classes.clear}>
        <ActionIcon onClick={handleClearFilters} aria-label='Clear filters'>
          <IconFilterX />
        </ActionIcon>
      </Tooltip>
    </div>
  );
};

export default WorkoutHistoryFilters;
